/**
 * Configuration Service
 * Handles mode detection, URL parameters, and environment settings
 */

import { createLogger } from './logger';
import { OperatingMode, OperatingModeConfig } from '../types';

const logger = createLogger('Config');

// API endpoints
const WORKER_API = 'https://1minutefunnel-player.thedesignatic.workers.dev';
const FUNNEL_DOMAIN = '1minutefunnel.com';

interface ConfigState {
  funnelName: string | null;
  operatingMode: OperatingMode;
  modeConfig: OperatingModeConfig;
  isLocalhost: boolean;
  urlParams: URLSearchParams;
}

let config: ConfigState | null = null;

/**
 * Check if running on localhost or local network
 */
function isLocalhost(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.')
  );
}

/**
 * Detect funnel name from URL
 * Priority: window.FUNNEL_ID (custom domains) > URL param (localhost only) > subdomain > path
 */
function detectFunnelName(params: URLSearchParams): string | null {
  // Check for injected FUNNEL_ID (set by worker for custom domains)
  // This handles cases like eval.infinitesouthraleigh.com → swing-eval
  if (typeof (window as any).FUNNEL_ID === 'string' && (window as any).FUNNEL_ID) {
    logger.info('Funnel from injected FUNNEL_ID (custom domain)', { funnel: (window as any).FUNNEL_ID });
    return (window as any).FUNNEL_ID;
  }

  const isLocal = isLocalhost();

  // URL param override - ONLY on localhost for security
  if (isLocal) {
    const funnelParam = params.get('funnel');
    if (funnelParam) {
      logger.info('Funnel from URL param (localhost)', { funnel: funnelParam });
      return funnelParam;
    }
  }

  // Try subdomain (e.g., test40.1minutefunnel.com)
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www') {
    logger.info('Funnel from subdomain', { funnel: parts[0] });
    return parts[0];
  }

  // Try path (e.g., /quizfunnels/test40/)
  const pathMatch = window.location.pathname.match(/\/quizfunnels\/([^/]+)/);
  if (pathMatch) {
    logger.info('Funnel from path', { funnel: pathMatch[1] });
    return pathMatch[1];
  }

  logger.warn('Could not detect funnel name');
  return null;
}

const MODE_STORAGE_KEY = 'player2_operating_mode';

/**
 * Detect operating mode from URL parameters or sessionStorage
 * Mode persists across pages within the same session
 */
function detectOperatingMode(params: URLSearchParams): OperatingMode {
  // Priority order: URL params > window flags (worker-injected) > sessionStorage > production

  // Check URL params first (allows explicit mode changes)
  if (params.get('preview') === 'true') {
    persistMode('preview');
    return 'preview';
  }

  // Check worker-injected preview flag (set by Player Worker's injectPreviewMode)
  if ((window as any).PREVIEW_MODE === true) {
    persistMode('preview');
    return 'preview';
  }
  if (params.get('inprogress') === 'true') {
    persistMode('in_progress');
    return 'in_progress';
  }
  if (params.get('test') === 'true') {
    persistMode('test');
    return 'test';
  }

  // Check sessionStorage for persisted mode
  try {
    const storedMode = sessionStorage.getItem(MODE_STORAGE_KEY) as OperatingMode | null;
    if (storedMode === 'test' || storedMode === 'in_progress' || storedMode === 'preview') {
      logger.debug('Using persisted mode from session', { mode: storedMode });
      return storedMode;
    }
  } catch {
    // Ignore storage errors
  }

  return 'production';
}

/**
 * Persist mode to sessionStorage
 */
function persistMode(mode: OperatingMode): void {
  try {
    if (mode === 'production') {
      sessionStorage.removeItem(MODE_STORAGE_KEY);
    } else {
      sessionStorage.setItem(MODE_STORAGE_KEY, mode);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get mode configuration based on operating mode
 */
function getModeConfig(mode: OperatingMode): OperatingModeConfig {
  switch (mode) {
    case 'production':
      return {
        mode: 'production',
        trackingEnabled: true,
        leadStorageEnabled: true,
        leadStoragePath: 'actual',
        dataSource: 'published',
        showModeBanner: false
      };
    case 'test':
      return {
        mode: 'test',
        trackingEnabled: false,
        leadStorageEnabled: true,
        leadStoragePath: 'test',
        dataSource: 'published',
        showModeBanner: true
      };
    case 'in_progress':
      return {
        mode: 'in_progress',
        trackingEnabled: false,
        leadStorageEnabled: false,
        leadStoragePath: null,
        dataSource: 'draft',
        showModeBanner: true
      };
    case 'preview':
      return {
        mode: 'preview',
        trackingEnabled: false,
        leadStorageEnabled: false,
        leadStoragePath: null,
        dataSource: 'published',
        showModeBanner: true
      };
  }
}

/**
 * Initialize configuration
 */
export function initConfig(): ConfigState {
  if (config) return config;

  const urlParams = new URLSearchParams(window.location.search);
  const isLocal = isLocalhost();
  const funnelName = detectFunnelName(urlParams);
  const operatingMode = detectOperatingMode(urlParams);
  const modeConfig = getModeConfig(operatingMode);

  config = {
    funnelName,
    operatingMode,
    modeConfig,
    isLocalhost: isLocal,
    urlParams
  };

  logger.info('Config initialized', {
    funnelName,
    operatingMode,
    isLocalhost: isLocal,
    trackingEnabled: modeConfig.trackingEnabled,
    leadStorageEnabled: modeConfig.leadStorageEnabled
  });

  return config;
}

/**
 * Get current configuration
 */
export function getConfig(): ConfigState {
  if (!config) {
    return initConfig();
  }
  return config;
}

/**
 * Get funnel name
 */
export function getFunnelName(): string | null {
  return getConfig().funnelName;
}

/**
 * Get operating mode
 */
export function getOperatingMode(): OperatingMode {
  return getConfig().operatingMode;
}

/**
 * Get mode configuration
 */
export function getModeConfiguration(): OperatingModeConfig {
  return getConfig().modeConfig;
}

/**
 * Check if tracking is enabled
 */
export function isTrackingEnabled(): boolean {
  return getConfig().modeConfig.trackingEnabled;
}

/**
 * Check if lead storage is enabled
 */
export function isLeadStorageEnabled(): boolean {
  return getConfig().modeConfig.leadStorageEnabled;
}

/**
 * Check if test mode
 */
export function isTestMode(): boolean {
  return getConfig().operatingMode === 'test';
}

/**
 * Check if in progress mode
 */
export function isInProgressMode(): boolean {
  return getConfig().operatingMode === 'in_progress';
}

/**
 * Check if preview mode
 */
export function isPreviewMode(): boolean {
  return getConfig().operatingMode === 'preview';
}

/**
 * Check if production mode
 */
export function isProductionMode(): boolean {
  return getConfig().operatingMode === 'production';
}

/**
 * Get API base URL
 */
export function getApiBase(): string {
  return WORKER_API;
}

/**
 * Get funnel API URL
 * Uses subdomain pattern: https://{funnelName}.1minutefunnel.com/quizfunnels/{funnelName}/funnel
 */
export function getFunnelApiUrl(): string {
  const funnelName = getFunnelName();
  const modeConfig = getModeConfiguration();

  // Use subdomain-based URL pattern
  let url = `https://${funnelName}.${FUNNEL_DOMAIN}/quizfunnels/${funnelName}/funnel`;

  // Add draft param to load draft JSON
  if (modeConfig.dataSource === 'draft') {
    url += '?draft=true';
  }

  return url;
}

/**
 * Get leads API URL
 */
export function getLeadsApiUrl(): string {
  return `${WORKER_API}/api/v1/leads`;
}

/**
 * Get progress tracking API URL
 */
export function getProgressApiUrl(): string {
  return `${WORKER_API}/api/v1/progress`;
}

/**
 * Get sessions API URL (page view tracking)
 */
export function getSessionsApiUrl(): string {
  return `${WORKER_API}/api/v1/sessions`;
}

/**
 * Get URL parameter
 */
export function getUrlParam(key: string): string | null {
  return getConfig().urlParams.get(key);
}

/**
 * Check if should reset state (clear saved progress)
 */
export function shouldReset(): boolean {
  return getConfig().urlParams.get('reset') === 'true';
}
